import * as React from 'react';
import furoApi from '../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';

import {ErrorResponse, RevenueSummary, RevenueSummaryRequest, RevenueSummaryResponse} from '../api/dtos/dtos';
import {ColumnProps} from 'antd/lib/table';
import locales from '../locales';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import * as paths from '../paths';
import {DatePicker, Table} from 'antd';
import * as moment from 'moment';
import {ErrorAlert} from '../common/ErrorAlert';
import * as formUtils from '../utils/form-utils';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableColumn, searchableSortableColumn, sortableColumn} from '../utils/table-utils';
import * as routerUtils from '../utils/router-utils';
import FormItem from 'antd/lib/form/FormItem';
import {MOMENT_YEAR_MONTH_FORMAT} from '../utils/date-utils';
import {renderFixedPriceColumn, renderUnitColumn} from '../utils/number-utils';
import ClientName from "../common/ClientName";

const {MonthPicker} = DatePicker;


interface SummaryPageState extends GridState<RevenueSummary> {
  error?: ErrorResponse;
  selectedYear: number;
  selectedMonth: number;
  revenueTotal?: number;
}

class RevenueSummaryPage extends React.Component<RouteComponentProps, SummaryPageState> {

  public state: SummaryPageState = {
    // Default initial grid state
    ...tableUtils.initialGridState(),

    // Initial sorting
    sortOrder: 'ascend',
    sortColumnKey: 'name',

    // Month picker default
    selectedYear: moment().get('year'),
    selectedMonth: moment().get('month'),

    // Override with cached state from Router
    ...this.props.location.state
  };

  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {selectedYear, selectedMonth, error, revenueTotal} = this.state;

    const columns: Array<ColumnProps<RevenueSummary>> = [
      searchableSortableColumn(locales.client, 'name', this.state, this.renderNameColumn),
      searchableColumn(locales.responsible_user, 'responsibleUser', this.state, r => r.client?.responsibleUser),
      {
        ...sortableColumn(locales.total_revenue, 'total', this.state, r => renderUnitColumn(r.total, locales.pcs)),
        className: 'align-right',
      }, {
        ...sortableColumn(locales.uninvoiced_revenue, 'uninvoicedTotal', this.state,
          r => renderUnitColumn(r.uninvoicedTotal, locales.pcs)),
        className: 'align-right',
      }
    ];

    const monthPickerDisabledDateFn = (current: moment.Moment | null): boolean => {
      return current !== null && current.isAfter(moment(), 'month');
    };

    const monthPickerValue = moment().year(selectedYear).month(selectedMonth);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <FormItem {...formUtils.formItemLayout} label={locales.month_capitalized}>
          <MonthPicker
            format={MOMENT_YEAR_MONTH_FORMAT}
            value={monthPickerValue}
            disabledDate={monthPickerDisabledDateFn}
            onChange={this.onMonthPickerChange}
            placeholder={locales.choose}
            allowClear={false}
          />
        </FormItem>

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.indexRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 576}}
        />

        {revenueTotal !== undefined && (
          <div style={{textAlign: 'right'}}>
            <table style={{marginLeft: 'auto'}}>
              <tbody>
              <tr>
                <td style={{paddingRight: 8}}>{locales.revenue_totals}</td>
                <td><b>{renderFixedPriceColumn(revenueTotal)}</b></td>
              </tr>
              </tbody>
            </table>
          </div>
        )}

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderNameColumn = (r: RevenueSummary): React.ReactNode => {
    return r.client && (
      <ClientName
        name={r.client.name}
        link={paths.clientRevenueSummaryPath(r.client.id!, this.state.selectedYear, this.state.selectedMonth)}
        warning={r.client.warning}
      />
    );
  };

  private onGridChange = (newState: GridState<RevenueSummary>) => {
    this.setState({...newState}, () => this.reload());
  };

  private onMonthPickerChange = (date: moment.Moment) => {
    this.setState({
      page: 1,
      selectedYear: date.get('year'),
      selectedMonth: date.get('month'),
    }, () => this.reload());
  };

  private reload = () => {
    const {selectedYear, selectedMonth} = this.state;
    this.setState({revenueTotal: undefined});
    const request: RevenueSummaryRequest = {
      grid: {
        ...tableUtils.toGridRequest(this.state),
      },
      year: selectedYear,
      month: selectedMonth,
    };
    const promise = Promise.all([
      furoApi.summaries.getRevenueSummaries(request),
      furoApi.summaries.getRevenueTotal({year: selectedYear, month: selectedMonth}),
    ])
      .then(([response, total]) => {
        this.onReloadSuccess(response, total.total);
      })
      .catch((error: ErrorResponse) => {
        this.onApiError(error);
      });
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: RevenueSummaryResponse, revenueTotal: number) => {
    this.setState({
      ...tableUtils.toGridState(response),
      revenueTotal,
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };
}

export default withRouter(RevenueSummaryPage);



// WEBPACK FOOTER //
// ./src/summary/RevenueSummaryPage.tsx